import { useContext, useEffect, useState } from 'react';

import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';

import { MAIN_CONTAINER_ID } from '../../constants';
import { DeviceContext } from '../../contexts';
import { ButtonTitleWithLoading } from '../ButtonTitleWithLoading';
import { ChatUser } from '../../types';
import { ChatSearchUsersAutocomplete } from './ChatSearchUsersAutocomplete';

interface Form {
  users: ChatUser[];
}

export const ChatAddMembersDialog = ({
  open,
  handleClose,
  searchChatUsers,
  existingMembers,
  addUsersToChat,
}: {
  open: boolean;
  handleClose: () => void;
  searchChatUsers: (searchQuery: string) => Promise<ChatUser[]>;
  existingMembers: ChatUser[];
  addUsersToChat: (users: ChatUser[]) => Promise<void>;
}) => {
  const { isMobile } = useContext(DeviceContext);
  const methods = useForm<Form>({
    defaultValues: {
      users: [],
    },
  });
  const { watch } = methods;
  const [searchItems, setSearchItems] = useState<ChatUser[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (data: Form) => {
    setLoading(true);
    await addUsersToChat(data.users);
    methods.reset();
    setLoading(false);
    handleClose();
  };

  const users = watch('users');

  const onSearch = async (search: string) => {
    try {
      const users = await searchChatUsers(search);
      setSearchItems(users.filter(it => !existingMembers.some(member => member.uid === it.uid)));
    } catch (e) {
      console.error(e);
      methods.setValue('users', []);
    }
  };

  useEffect(() => {
    if (open) {
      onSearch('');
    }
  }, [open]);

  return (
    <Dialog
      container={document.getElementById(MAIN_CONTAINER_ID)}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={handleClose}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <DialogTitle>Add members</DialogTitle>

          <DialogContent>
            <ChatSearchUsersAutocomplete
              selectedItems={users}
              users={searchItems}
              onSearch={onSearch}
              label="Select chat members"
            />
          </DialogContent>

          <DialogActions>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>

            <Button
              style={{ width: 130 }}
              disabled={!users.length || loading}
              onClick={methods.handleSubmit(handleSubmit)}
              color="primary"
              variant="outlined"
            >
              <ButtonTitleWithLoading title="Add members" loaderVariant="primary" loading={loading} />
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
